import { getMattersTableColumns } from "@/components/features/matters/MattersTable/matters-table-columns";
import { MassTortMatterRowContextMenu } from "@/components/features/mass-torts/MassTortMatterRowContextMenu";
import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import { searchMattersQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import { keepPreviousSearchData } from "@/lib/data/react-query/search/scope";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { MATTER_SEARCH_SORT_FIELDS } from "@/lib/utils/matters/matter-search-sort";
import {
  getSearchMattersCount,
  type MatterSearchResult,
} from "@/lib/data/requests/matters/search-matters";
import { type MassTortMatterSearchStatusCategory } from "@/lib/data/requests";
import { type tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import { type DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useQuery } from "@tanstack/react-query";
import {
  functionalUpdate,
  getCoreRowModel,
  sortingFns,
  useReactTable,
  type ColumnDef,
  type Row,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";

export interface MassTortMattersTableProps {
  massTortId: string | undefined;
  category: MassTortMatterSearchStatusCategory;
  searchBar?: React.ReactNode;
  tableButtons?: React.ReactNode;
  onRowClick: (row: Row<MatterSearchResult>) => void;
  tableName: (typeof tanstackTableNames)[keyof typeof tanstackTableNames];
}

export const MassTortMattersTable = ({
  massTortId,
  category,
  searchBar,
  tableButtons,
  onRowClick,
  tableName,
}: MassTortMattersTableProps) => {
  const columns = useMemo<ColumnDef<MatterSearchResult>[]>(
    () =>
      getMattersTableColumns({
        columnHeaders: "plain",
        variant: "massTort",
      }),
    [],
  );
  const columnIds = useMemo(
    () =>
      columns
        .map((column) => column.id)
        .filter((id): id is string => Boolean(id)),
    [columns],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 250);
  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(tableName);
    return stored.sorting ?? [{ id: "Date Created", desc: true }];
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(tableName);
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 10,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(tableName);
    const order = stored.columnOrder;
    if (order?.length) {
      return [...order, ...columnIds.filter((id) => !order.includes(id))];
    }
    return columnIds;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(tableName);
      return {
        "Date Created": false,
        "Date Modified": false,
        ...stored.columnVisibility,
        Title: true,
      };
    },
  );

  const {
    data: mattersData,
    isLoading: isMattersLoading,
    isFetching: isMattersFetching,
    isError: isMattersError,
    isPlaceholderData,
  } = useQuery({
    ...searchMattersQueryOptions({
      massTortId,
      statusCategories: [category],
      search: debouncedSearchQuery.trim() || undefined,
      pageNumber: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sort: sorting[0] ? MATTER_SEARCH_SORT_FIELDS[sorting[0].id] : undefined,
      order: sorting[0] ? (sorting[0].desc ? "desc" : "asc") : undefined,
    }),
    enabled: Boolean(massTortId),
    placeholderData: keepPreviousSearchData,
  });
  const matters = useMemo(() => mattersData?.data ?? [], [mattersData]);
  const totalCount = getSearchMattersCount(mattersData) ?? 0;

  useEffect(() => {
    if (
      !mattersData ||
      isMattersFetching ||
      isPlaceholderData ||
      isMattersError
    )
      return;
    setPagination((prev) => {
      const lastPageIndex = Math.max(
        0,
        Math.ceil(totalCount / prev.pageSize) - 1,
      );
      return prev.pageIndex > lastPageIndex
        ? { ...prev, pageIndex: lastPageIndex }
        : prev;
    });
  }, [
    mattersData,
    totalCount,
    isMattersFetching,
    isPlaceholderData,
    isMattersError,
  ]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [massTortId, category, debouncedSearchQuery]);

  useEffect(() => {
    setColumnOrder((prev) => {
      const next = [
        ...prev.filter((id) => columnIds.includes(id)),
        ...columnIds.filter((id) => !prev.includes(id)),
      ];
      return next;
    });
  }, [columnIds]);

  const table = useReactTable({
    data: matters,
    getRowId: (row) => row.id,
    manualPagination: true,
    manualSorting: true,
    sortingFns: { customDateTime: sortingFns.datetime },
    rowCount: totalCount,
    columns,
    autoResetPageIndex: false,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setSorting((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(tableName, { sorting: next });
        return next;
      });
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(tableName, {
          pagination: { pageSize: next.pageSize },
        });
        return next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(tableName, { columnOrder: next });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Title: true };
        persistTanstackTablePrefs(tableName, { columnVisibility: merged });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const sensors = useMattersStyleTableColumnDragSensors();

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(tableName, { columnOrder: next });
      return next;
    });
  };

  return (
    <MattersStyleTableShell
      scrollableBody
      table={table}
      columnOrder={columnOrder}
      columnIdCountBaseline={columnIds.length}
      onColumnDragEnd={handleColumnDragEnd}
      sensors={sensors}
      search={{
        value: searchQuery,
        onChange: (value) => {
          setSearchQuery(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        },
        placeholder: "Search matters...",
        maxLength: 200,
      }}
      toolbarTrailing={tableButtons}
      toolbarAfter={searchBar}
      isLoading={isMattersLoading}
      isFetching={isMattersFetching}
      isError={isMattersError}
      errorMessage="Unable to load matters."
      emptyMessage="No matters found."
      totalFilteredRowCount={totalCount}
      entityNamePlural="matters"
      sorting={sorting}
      tableProps={{
        "data-table-name": tableName,
      }}
      getTableRowProps={(row) => ({
        className: "cursor-pointer",
        onClick: () => {
          onRowClick(row);
        },
      })}
      renderContextMenu={(row, trigger) => (
        <MassTortMatterRowContextMenu matter={row.original} trigger={trigger} />
      )}
    />
  );
};
